import { Plugin } from 'vite';
import * as path from 'path';
import fs from 'fs-extra';

// Helper to only write files when content changes to avoid unnecessary HMR triggers
async function writeFileIfChanged(filePath: string, content: string): Promise<boolean> {
	try {
		const exists = await fs.pathExists(filePath);
		if (exists) {
			const existingContent = await fs.readFile(filePath, 'utf-8');
			if (existingContent === content) {
				return false; // No change needed
			}
		}
		await fs.writeFile(filePath, content, 'utf-8');
		return true; // File was written
	} catch (error) {
		// If there's an error reading, just write the file
		await fs.writeFile(filePath, content, 'utf-8');
		return true;
	}
}

interface MetaDefaults {
	title?: string;
	titleTemplate?: string;
	description?: string;
	keywords?: string[];
	author?: string;
	viewport?: string;
	charset?: string;
	themeColor?: string;

	// Open Graph
	openGraph?: {
		siteName?: string;
		type?: string;
		locale?: string;
		image?: string;
		imageWidth?: number;
		imageHeight?: number;
		imageAlt?: string;
		url?: string;
	};

	// Twitter Card
	twitter?: {
		card?: string;
		site?: string;
		creator?: string;
		image?: string;
		imageAlt?: string;
	};

	// Additional meta tags
	meta?: Array<{
		name?: string;
		property?: string;
		content: string;
		key?: string;
	}>;
}

export interface MetaConfig extends Partial<MetaDefaults> {
	enabled?: boolean;
	defaults?: MetaDefaults;
}

export function createMetaPlugin(cwd: string, config: MetaConfig = {}): Plugin {
	// Default configuration
	const defaultConfig = {
		enabled: true,
		defaults: {
			title: 'Frame App',
			titleTemplate: '%s | Frame App',
			description: 'A modern Vue.js application built with Frame',
			keywords: ['vue', 'javascript', 'typescript', 'frame'],
			author: 'Frame Developer',
			viewport: 'width=device-width, initial-scale=1.0',
			charset: 'utf-8',
			themeColor: '#42b883',

			openGraph: {
				siteName: 'Frame App',
				type: 'website',
				locale: 'en_US',
				imageWidth: 1200,
				imageHeight: 630,
			},

			twitter: {
				card: 'summary_large_image',
				site: '@frameapp',
				creator: '@frameapp',
			}
		}
	};

	// Handle both flat and nested config structures for better UX.
	// MERGE them (flat top-level keys win) instead of preferring one — ConfigLoader
	// pre-seeds `config.defaults`, so a consumer's flat `meta.title` must not be shadowed.
	const { defaults: nestedDefaults, enabled: _enabled, ...flatDefaults } = (config as any) || {};
	const userDefaults = { ...(nestedDefaults || {}), ...flatDefaults } as MetaDefaults;



	// Deep merge user config with defaults
	const metaConfig = {
		enabled: config.enabled ?? defaultConfig.enabled,
		defaults: {
			...defaultConfig.defaults,
			...userDefaults,
			openGraph: {
				...defaultConfig.defaults.openGraph,
				...userDefaults?.openGraph
			},
			twitter: {
				...defaultConfig.defaults.twitter,
				...userDefaults?.twitter
			}
		}
	};

	return {
		name: 'frame-meta',

		async buildStart() {
			if (metaConfig.enabled !== false) {
				await generateMetaHelpers(cwd, metaConfig);
			}
		},

		configureServer(server) {
			// Watch for meta configuration changes
			const configFiles = ['frame.config.js', 'frame.config.mjs', 'frame.config.ts'];

			configFiles.forEach(configFile => {
				const configPath = path.join(cwd, configFile);
				if (fs.existsSync(configPath)) {
					server.watcher.add(configPath);
				}
			});

			server.watcher.on('change', async (file) => {
				if (configFiles.some(cf => file.endsWith(cf))) {
					console.log('[Frame Meta] Configuration changed, regenerating meta helpers...');

					// Reload the configuration from the file
					const { ConfigLoader } = await import('../config/ConfigLoader.js');
					const configLoader = new ConfigLoader(cwd);
					const frameConfig = await configLoader.load();
					const updatedConfig = frameConfig.meta || {};

					// Handle both flat and nested config structures (flat keys win — see buildStart).
					const { defaults: _nd, enabled: _en, ...flat } = (updatedConfig as any) || {};
					const updatedUserDefaults = { ...(_nd || {}), ...flat } as MetaDefaults;

					// Deep merge with defaults again
					const updatedMetaConfig = {
						enabled: updatedConfig.enabled ?? defaultConfig.enabled,
						defaults: {
							...defaultConfig.defaults,
							...updatedUserDefaults,
							openGraph: {
								...defaultConfig.defaults.openGraph,
								...updatedUserDefaults?.openGraph
							},
							twitter: {
								...defaultConfig.defaults.twitter,
								...updatedUserDefaults?.twitter
							}
						}
					};


					await generateMetaHelpers(cwd, updatedMetaConfig);
				}
			});
		}
	};
}

async function generateMetaHelpers(cwd: string, config: MetaConfig): Promise<void> {
	const frameDir = path.join(cwd, 'src', '.frame');
	await fs.ensureDir(frameDir);

	const metaHelpersContent = `// Auto-generated meta helpers - do not edit manually
import { useHead } from '@vueuse/head';
import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';

export interface MetaOptions {
	title?: string;
	description?: string;
	keywords?: string[];
	author?: string;
	image?: string;
	imageAlt?: string;
	url?: string;
	type?: string;
	locale?: string;
	siteName?: string;
	themeColor?: string;
	
	// Custom meta tags
	meta?: Array<{
		name?: string;
		property?: string;
		content: string;
		key?: string;
	}>;
	
	// Twitter specific
	twitter?: {
		card?: string;
		site?: string;
		creator?: string;
		image?: string;
		imageAlt?: string;
	};
}

// Default configuration
export const defaultMetaConfig = ${JSON.stringify(config.defaults, null, 2)};

/**
 * Frame Meta Management Composable
 * 
 * Provides easy-to-use meta tag management with support for:
 * - Dynamic page titles
 * - Open Graph tags for social media
 * - Twitter Card tags
 * - Custom meta tags
 * - SEO optimization
 */
export function useFrameMeta(options?: MetaOptions) {
	const route = useRoute();
	
	// Reactive meta state
	const metaState = reactive({
		title: options?.title || defaultMetaConfig.title,
		description: options?.description || defaultMetaConfig.description,
		keywords: options?.keywords || defaultMetaConfig.keywords,
		author: options?.author || defaultMetaConfig.author,
		image: options?.image || defaultMetaConfig.openGraph?.image,
		imageAlt: options?.imageAlt || defaultMetaConfig.openGraph?.imageAlt,
		url: options?.url || (typeof window !== 'undefined' ? window.location.href : ''),
		type: options?.type || defaultMetaConfig.openGraph?.type,
		locale: options?.locale || defaultMetaConfig.openGraph?.locale,
		siteName: options?.siteName || defaultMetaConfig.openGraph?.siteName,
		themeColor: options?.themeColor || defaultMetaConfig.themeColor,
		customMeta: options?.meta || [],
		twitter: {
			...defaultMetaConfig.twitter,
			...options?.twitter
		}
	});

	// Computed meta tags
	const metaTags = computed(() => {
		const tags: any[] = [
			// Basic meta tags
			{ name: 'description', content: metaState.description },
			{ name: 'keywords', content: metaState.keywords?.join(', ') },
			{ name: 'author', content: metaState.author },
			{ name: 'viewport', content: defaultMetaConfig.viewport },
			{ name: 'theme-color', content: metaState.themeColor },
			
			// Open Graph tags
			{ property: 'og:title', content: metaState.title },
			{ property: 'og:description', content: metaState.description },
			{ property: 'og:type', content: metaState.type },
			{ property: 'og:url', content: metaState.url },
			{ property: 'og:site_name', content: metaState.siteName },
			{ property: 'og:locale', content: metaState.locale },
			
			// Twitter Card tags
			{ name: 'twitter:card', content: metaState.twitter.card },
			{ name: 'twitter:title', content: metaState.title },
			{ name: 'twitter:description', content: metaState.description },
			{ name: 'twitter:site', content: metaState.twitter.site },
			{ name: 'twitter:creator', content: metaState.twitter.creator }
		];

		// Add image tags if image is provided
		if (metaState.image) {
			tags.push(
				{ property: 'og:image', content: metaState.image },
				{ property: 'og:image:alt', content: metaState.imageAlt },
				{ property: 'og:image:width', content: defaultMetaConfig.openGraph?.imageWidth?.toString() },
				{ property: 'og:image:height', content: defaultMetaConfig.openGraph?.imageHeight?.toString() },
				{ name: 'twitter:image', content: metaState.image },
				{ name: 'twitter:image:alt', content: metaState.imageAlt }
			);
		}

		// Add custom meta tags
		if (metaState.customMeta.length > 0) {
			tags.push(...metaState.customMeta);
		}

		return tags.filter(tag => tag.content); // Remove empty tags
	});

	// Use Vue Head to manage meta tags
	useHead({
		title: computed(() => {
			const template = defaultMetaConfig.titleTemplate;
			return template && metaState.title !== defaultMetaConfig.title
				? template.replace('%s', metaState.title)
				: metaState.title;
		}),
		meta: metaTags,
		htmlAttrs: {
			lang: computed(() => metaState.locale?.split('_')[0] || 'en')
		}
	});

	// Update methods
	const updateTitle = (title: string) => {
		metaState.title = title;
	};

	const updateDescription = (description: string) => {
		metaState.description = description;
	};

	const updateImage = (image: string, alt?: string) => {
		metaState.image = image;
		if (alt) metaState.imageAlt = alt;
	};

	const updateUrl = (url: string) => {
		metaState.url = url;
	};

	const updateMeta = (meta: Partial<MetaOptions>) => {
		Object.assign(metaState, meta);
	};

	const addCustomMeta = (meta: { name?: string; property?: string; content: string; key?: string }) => {
		const existing = metaState.customMeta.findIndex(m => m.key === meta.key || (m.name === meta.name && m.property === meta.property));
		if (existing >= 0) {
			metaState.customMeta[existing] = meta;
		} else {
			metaState.customMeta.push(meta);
		}
	};

	const removeCustomMeta = (key: string) => {
		const index = metaState.customMeta.findIndex(m => m.key === key);
		if (index >= 0) {
			metaState.customMeta.splice(index, 1);
		}
	};

	// Auto-derive URL from the route ONLY when the caller didn't pass an explicit url.
	// An explicit options.url is the canonical/absolute URL (e.g. https://site.com/) and must
	// win — otherwise it gets clobbered by window.location, which during prerender is the
	// internal render origin (http://host:8080/?__bodify_render=1), leaking into og:url.
	if (route && !options?.url) {
		const currentUrl = computed(() => {
			const base = typeof window !== 'undefined' ? window.location.origin : '';
			return base + route.fullPath;
		});

		metaState.url = currentUrl.value;
	}

	return {
		// State
		metaState,
		
		// Update methods
		updateTitle,
		updateDescription,
		updateImage,
		updateUrl,
		updateMeta,
		addCustomMeta,
		removeCustomMeta,
		
		// Getters
		getCurrentMeta: () => ({ ...metaState }),
		getMetaTags: () => metaTags.value
	};
}

/**
 * Preset meta configurations for common page types
 */
export const metaPresets = {
	homepage: {
		title: 'Home',
		description: 'Welcome to our amazing application built with Frame',
		type: 'website'
	},
	
	about: {
		title: 'About Us',
		description: 'Learn more about our company and mission',
		type: 'website'
	},
	
	blog: {
		title: 'Blog',
		description: 'Read our latest articles and insights',
		type: 'website'
	},
	
	article: {
		title: 'Article',
		description: 'Read this amazing article',
		type: 'article'
	},
	
	profile: {
		title: 'Profile',
		description: 'User profile page',
		type: 'profile'
	},
	
	product: {
		title: 'Product',
		description: 'Check out this amazing product',
		type: 'product'
	}
};

/**
 * Generate meta tags for a specific page type
 */
export function generatePageMeta(pageType: keyof typeof metaPresets, overrides?: Partial<MetaOptions>) {
	const preset = metaPresets[pageType];
	return {
		...preset,
		...overrides
	};
}

/**
 * SEO utility functions
 */
export const seoUtils = {
	/**
	 * Generate optimized title with proper length
	 */
	optimizeTitle(title: string, maxLength: number = 60): string {
		if (title.length <= maxLength) return title;
		return title.substring(0, maxLength - 3) + '...';
	},

	/**
	 * Generate optimized description
	 */
	optimizeDescription(description: string, maxLength: number = 160): string {
		if (description.length <= maxLength) return description;
		return description.substring(0, maxLength - 3) + '...';
	},

	/**
	 * Generate keywords from text
	 */
	generateKeywords(text: string, maxKeywords: number = 10): string[] {
		return text
			.toLowerCase()
			.split(/\\W+/)
			.filter(word => word.length > 3)
			.slice(0, maxKeywords);
	},

	/**
	 * Validate image URL for social media
	 */
	isValidImageUrl(url: string): boolean {
		return /\\.(jpg|jpeg|png|gif|webp)$/i.test(url);
	},

	/**
	 * Generate canonical URL
	 */
	generateCanonicalUrl(path: string): string {
		const base = typeof window !== 'undefined' ? window.location.origin : '';
		return base + (path.startsWith('/') ? path : '/' + path);
	}
};

/**
 * Initialize router-based meta tag management
 * Sets up navigation guards to automatically update meta tags when routes change
 * 
 * Handles all MetaOptions properties:
 * - title, description, keywords, author
 * - image, imageAlt, url, type, locale, siteName, themeColor
 * - openGraph (full object with all OG properties)
 * - twitter (card, site, creator, image, imageAlt)
 * - meta (custom meta tags array)
 */
export function initRouterMeta(router: any) {
	router.afterEach((to: any) => {
		// Helper to update meta tags
		const updateMetaTag = (selector: string, content: string) => {
			if (!content) return; // Skip empty values
			let element = document.querySelector(selector);
			if (element) {
				element.setAttribute('content', content);
			}
		};
		
		// Helper to add/update custom meta tag
		const updateCustomMetaTag = (nameOrProperty: string, content: string, isProperty: boolean = false) => {
			if (!content) return;
			const attr = isProperty ? 'property' : 'name';
			const selector = \`meta[\${attr}="\${nameOrProperty}"]\`;
			let element = document.querySelector(selector);
			
			if (element) {
				element.setAttribute('content', content);
			} else {
				// Create new meta tag if it doesn't exist
				const meta = document.createElement('meta');
				meta.setAttribute(attr, nameOrProperty);
				meta.setAttribute('content', content);
				document.head.appendChild(meta);
			}
		};
		
		// 1. Update TITLE (with template support)
		const routeTitle = to.meta?.title;
		if (routeTitle) {
			const template = defaultMetaConfig.titleTemplate;
			const defaultTitle = defaultMetaConfig.title;
			
			let finalTitle = routeTitle;
			if (template && routeTitle !== defaultTitle) {
				const templateSuffix = template.replace('%s', '').trim();
				if (templateSuffix && !routeTitle.includes(templateSuffix)) {
					finalTitle = template.replace('%s', routeTitle);
				}
			}
			
			document.title = finalTitle;
			updateMetaTag('meta[property="og:title"]', finalTitle);
			updateMetaTag('meta[name="twitter:title"]', finalTitle);
		}
		
		// 2. Update DESCRIPTION
		if (to.meta?.description) {
			updateMetaTag('meta[name="description"]', to.meta.description);
			updateMetaTag('meta[property="og:description"]', to.meta.description);
			updateMetaTag('meta[name="twitter:description"]', to.meta.description);
		}
		
		// 3. Update KEYWORDS
		if (to.meta?.keywords) {
			const keywordsContent = Array.isArray(to.meta.keywords) 
				? to.meta.keywords.join(', ') 
				: to.meta.keywords;
			updateMetaTag('meta[name="keywords"]', keywordsContent);
		}
		
		// 4. Update AUTHOR
		if (to.meta?.author) {
			updateMetaTag('meta[name="author"]', to.meta.author);
		}
		
		// 5. Update THEME COLOR
		if (to.meta?.themeColor) {
			updateMetaTag('meta[name="theme-color"]', to.meta.themeColor);
		}
		
		// 6. Update SHORTHAND properties (image, imageAlt, url, type, locale, siteName)
		// These are convenience properties that map to OpenGraph
		const shorthandImage = to.meta?.image;
		const shorthandImageAlt = to.meta?.imageAlt;
		const shorthandUrl = to.meta?.url;
		const shorthandType = to.meta?.type;
		const shorthandLocale = to.meta?.locale;
		const shorthandSiteName = to.meta?.siteName;
		
		// 7. Update OPENGRAPH properties (these take precedence over shorthand)
		const og = to.meta?.openGraph || {};
		
		// Image
		const finalImage = og.image || shorthandImage;
		if (finalImage) {
			updateMetaTag('meta[property="og:image"]', finalImage);
			updateMetaTag('meta[name="twitter:image"]', finalImage);
		}
		
		// Image Alt
		const finalImageAlt = og.imageAlt || shorthandImageAlt;
		if (finalImageAlt) {
			updateMetaTag('meta[property="og:image:alt"]', finalImageAlt);
			updateMetaTag('meta[name="twitter:image:alt"]', finalImageAlt);
		}
		
		// Image dimensions
		if (og.imageWidth) {
			updateMetaTag('meta[property="og:image:width"]', og.imageWidth.toString());
		}
		if (og.imageHeight) {
			updateMetaTag('meta[property="og:image:height"]', og.imageHeight.toString());
		}
		
		// Type
		const finalType = og.type || shorthandType;
		if (finalType) {
			updateMetaTag('meta[property="og:type"]', finalType);
		}
		
		// URL (canonical)
		const finalUrl = og.url || shorthandUrl;
		if (finalUrl) {
			updateMetaTag('meta[property="og:url"]', finalUrl);
			// Also update canonical link if it exists
			const canonical = document.querySelector('link[rel="canonical"]');
			if (canonical) {
				canonical.setAttribute('href', finalUrl);
			}
		}
		
		// Site Name
		const finalSiteName = og.siteName || shorthandSiteName;
		if (finalSiteName) {
			updateMetaTag('meta[property="og:site_name"]', finalSiteName);
		}
		
		// Locale
		const finalLocale = og.locale || shorthandLocale;
		if (finalLocale) {
			updateMetaTag('meta[property="og:locale"]', finalLocale);
			// Also update html lang attribute
			const htmlLang = finalLocale.split('_')[0];
			if (htmlLang) {
				document.documentElement.setAttribute('lang', htmlLang);
			}
		}
		
		// 8. Update TWITTER-specific properties
		if (to.meta?.twitter) {
			const twitter = to.meta.twitter;
			
			if (twitter.card) {
				updateMetaTag('meta[name="twitter:card"]', twitter.card);
			}
			
			if (twitter.site) {
				updateMetaTag('meta[name="twitter:site"]', twitter.site);
			}
			
			if (twitter.creator) {
				updateMetaTag('meta[name="twitter:creator"]', twitter.creator);
			}
			
			// Twitter image (only if not already set by og:image or shorthand)
			if (twitter.image && !finalImage) {
				updateMetaTag('meta[name="twitter:image"]', twitter.image);
			}
			
			// Twitter image alt (only if not already set)
			if (twitter.imageAlt && !finalImageAlt) {
				updateMetaTag('meta[name="twitter:image:alt"]', twitter.imageAlt);
			}
		}
		
		// 9. Update CUSTOM meta tags
		if (to.meta?.meta && Array.isArray(to.meta.meta)) {
			to.meta.meta.forEach((customMeta: any) => {
				if (customMeta.name) {
					updateCustomMetaTag(customMeta.name, customMeta.content, false);
				} else if (customMeta.property) {
					updateCustomMetaTag(customMeta.property, customMeta.content, true);
				}
			});
		}
	});
};
`;

	const metaHelpersPath = path.join(frameDir, 'meta-helpers.ts');
	const wasWritten = await writeFileIfChanged(metaHelpersPath, metaHelpersContent);

	if (wasWritten) {
		console.log('[Frame Meta] Meta helpers generated at:', metaHelpersPath);
	}
}

/**
 * Create meta plugin with sensible defaults
 */
export function createDefaultMetaPlugin(cwd: string): Plugin {
	return createMetaPlugin(cwd, {
		enabled: true,
		defaults: {
			title: 'Frame App',
			titleTemplate: '%s | Frame App',
			description: 'A modern Vue.js application built with Frame',
			keywords: ['vue', 'javascript', 'typescript', 'frame'],
			author: 'Frame Developer',
			viewport: 'width=device-width, initial-scale=1.0',
			charset: 'utf-8',
			themeColor: '#42b883',

			openGraph: {
				siteName: 'Frame App',
				type: 'website',
				locale: 'en_US',
				imageWidth: 1200,
				imageHeight: 630,
			},

			twitter: {
				card: 'summary_large_image',
				site: '@frameapp',
				creator: '@frameapp',
			}
		}
	});
} 